import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { DxCard, DxCardContent, DxCardHeader, DxList } from '@dvag/design-system-react';

import { SectionListData } from 'type/sectionList';
import { checkIcon } from 'utils/util';
import { dashboardUtil } from 'service/model/dashboardUtil';
import { Item } from 'component/BottomCards/Card/Item';
import './style.css';
import { rightCardItemList, useUtil } from './util';
import { useGetCustomerPortalStatus } from 'hooks/useGetCustomerPortalStatus';
import { useGetHouseholdDashboard } from 'hooks/useGetHouseholdDashboard';
import { FamilyApproval } from 'component/FamilyApproval/FamilyApproval';
import { QrApprovalProvider } from 'component/FamilyApproval/QrApprovalContext';

const { GOLDRICHTIGE_EMPFEHLUNG, TRAUMBERUF } = dashboardUtil;

export const RightBottomCard = () => {
  const { householdId, section } = useParams();
  const navigateInternal = useNavigate();

  const pathToDashboard = `/haushalt/${householdId}/uebersicht`;

  const { openKundenzugangTool } = useUtil(householdId as string);
  const customerPortalStatus = useGetCustomerPortalStatus(householdId ?? '');
  const householdDashboard = useGetHouseholdDashboard();
  const personList = useMemo(
    () => householdDashboard?.data?.personList || [],
    [householdDashboard?.data?.personList],
  );

  const rightCardPermissions = {
    kundenzugangPermission: true,
    recommendationPermission: true,
    teambuildingPermission: true,
  };

  const { rightCardList, goldrichtigeEmpfehlungList, traumberufList } = rightCardItemList(
    rightCardPermissions,
    householdId,
    customerPortalStatus,
    openKundenzugangTool,
  );

  const cardTitle = {
    defaultSection: {
      permission: true,
      title: 'Zusammenarbeit',
      dataTestId: 'zusammenarbeit-back-button',
    },
    [GOLDRICHTIGE_EMPFEHLUNG]: {
      permission: true,
      title: 'Goldrichtige Empfehlung',
      dataTestId: 'recommendation-back-button',
    },
    [TRAUMBERUF]: {
      permission: true,
      title: 'Traumberuf',
      dataTestId: 'dream-job-back-button',
    },
  };

  const list = {
    defaultSection: rightCardList,
    [GOLDRICHTIGE_EMPFEHLUNG]: goldrichtigeEmpfehlungList,
    [TRAUMBERUF]: traumberufList,
  };

  const isSectionInItemList =
    !!section && Object.keys(list).includes(section) && cardTitle[section]?.permission;
  const sectionKey = isSectionInItemList ? section : 'defaultSection';
  const currentSection = list[sectionKey];

  const onItemClick = (item: SectionListData) => {
    if (!householdId) {
      return;
    }

    if (item.onClick) {
      item.onClick();
    }
  };

  return (
    <DxCard style={{ overflow: 'hidden' }} data-testid="right-bottom-card-container">
      {isSectionInItemList ? (
        <DxCardHeader
          label={cardTitle[sectionKey].title}
          icon={checkIcon('pfeil-links')}
          iconcolor="default"
          onClick={() => navigateInternal(pathToDashboard)}
          style={{ cursor: 'pointer' }}
          data-testid={cardTitle[sectionKey].dataTestId}
        />
      ) : (
        <DxCardHeader
          data-testid={cardTitle['defaultSection'].dataTestId}
          label={cardTitle['defaultSection'].title}
        />
      )}
      <DxCardContent className="dashboard-card-content">
        <div className="flex column">
          <DxList size="m">
            {currentSection?.map((item) => (
              <Item
                id={item.id}
                key={item.id}
                onItemClick={() => onItemClick(item)}
                title={item.title}
                cursorStyle={householdId ? 'pointer' : 'default'}
                icon={item.icon}
              />
            ))}
          </DxList>
        </div>
      </DxCardContent>
      <QrApprovalProvider>
        <FamilyApproval personList={personList} householdId={householdId} />
      </QrApprovalProvider>
    </DxCard>
  );
};
